import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlignCenter, AlignLeft, AlignRight, type LucideIcon } from 'lucide-react'
import type { CanvasTextAlign } from '@/src/renderer/lib/db/models'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import type { CanvasOptionsBarPlacement } from './CanvasOptionsBar.types'

/** Die drei Ausrichtungen mit ihrem Symbol — Knopf und Menü zeigen dasselbe. */
export const CANVAS_TEXT_ALIGNS: readonly { key: CanvasTextAlign; icon: LucideIcon }[] = [
  { key: 'left', icon: AlignLeft },
  { key: 'center', icon: AlignCenter },
  { key: 'right', icon: AlignRight },
]

export interface CanvasTextAlignMenuProps {
  value: CanvasTextAlign
  placement: CanvasOptionsBarPlacement
  ref?: React.Ref<HTMLDivElement>
  onPick: (align: CanvasTextAlign) => void
  leaving?: boolean
}

/** Die **Ausrichtung** des Texts: links, mittig, rechts — sie gilt immer dem ganzen Text. */
export const CanvasTextAlignMenu: React.FC<CanvasTextAlignMenuProps> = ({ value, placement, ref, onPick, leaving = false }) => {
  const { t } = useTranslation()
  return (
    <div
      ref={ref}
      className={`canvas-options-bar__flyout canvas-options-bar__flyout--dark canvas-options-bar__flyout--center canvas-options-bar__flyout--${placement} canvas-text-align-menu`}
      data-leaving={leaving ? '' : undefined}
      role="listbox"
      aria-label={t('projectCanvas.text.align')}
    >
      {CANVAS_TEXT_ALIGNS.map(({ key, icon: Icon }) => {
        const label = t(`projectCanvas.text.alignTo.${key}`)
        return (
          <WithTooltip key={key} content={label}>
            <button
              type="button"
              role="option"
              aria-label={label}
              aria-selected={value === key}
              className={`canvas-options-bar__button${value === key ? ' is-active' : ''}`}
              onClick={() => onPick(key)}
            >
              <Icon size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        )
      })}
    </div>
  )
}
